/**
 * @fileoverview Modal for connecting a new bank account via Enable Banking.
 * @module features/bank-connections/components/ConnectBankModal
 */

import { ReactElement, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Building2, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Input,
  Skeleton,
} from '@/shared/components/ui'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/shared/utils/cn'
import { useBanks, useStartBankAuthorization } from '../hooks/useBankConnections'
import type { BankDto } from '../schemas/bankConnectionsSchema'

/**
 * Supported countries for bank connections.
 */
const SUPPORTED_COUNTRIES = [
  { code: 'PL', name: 'Poland' },
  { code: 'DE', name: 'Germany' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'AT', name: 'Austria' },
  { code: 'SE', name: 'Sweden' },
] as const

/**
 * Props for {@link ConnectBankModal}.
 */
export interface ConnectBankModalProps {
  /** Whether the modal is open. */
  open: boolean
  /** Callback when the modal should close. */
  onOpenChange: (open: boolean) => void
  /** Optional pre-selected country code. */
  defaultCountry?: string
}

/**
 * ConnectBankModal
 *
 * Modal allowing users to select a country and bank to connect.
 * Starts OAuth flow when a bank is selected.
 *
 * @component
 */
export function ConnectBankModal({
  open,
  onOpenChange,
  defaultCountry = 'PL',
}: ConnectBankModalProps): ReactElement {
  const { t } = useTranslation('features-bank-connections')
  const [country, setCountry] = useState(defaultCountry)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedBank, setSelectedBank] = useState<BankDto | null>(null)

  const banksQuery = useBanks(country, open)
  const authorizeMutation = useStartBankAuthorization()

  const filteredBanks = useMemo(() => {
    if (!banksQuery.data) return []
    if (!searchQuery.trim()) return banksQuery.data
    const query = searchQuery.toLowerCase()
    return banksQuery.data.filter(
      (bank) =>
        bank.name.toLowerCase().includes(query) ||
        (bank.bic && bank.bic.toLowerCase().includes(query))
    )
  }, [banksQuery.data, searchQuery])

  const handleCountryChange = (newCountry: string): void => {
    setCountry(newCountry)
    setSearchQuery('')
    setSelectedBank(null)
  }

  const handleBankSelect = async (bank: BankDto): Promise<void> => {
    setSelectedBank(bank)
    try {
      const result = await authorizeMutation.mutateAsync({
        bankName: bank.name,
        country,
      })
      // Redirect to bank authorization page
      window.location.assign(result.authorizationUrl)
    } catch {
      setSelectedBank(null)
    }
  }

  const handleOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) {
      // Reset state when closing
      setSearchQuery('')
      setSelectedBank(null)
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] sm:max-h-[80vh] flex flex-col p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">
            {t('connect.title', { defaultValue: 'Connect Bank Account' })}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t('connect.description', {
              defaultValue: 'Select your country and bank to securely connect your account.',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 sm:space-y-4">
          {/* Country selector */}
          <div className="space-y-1.5 sm:space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t('connect.country', { defaultValue: 'Country' })}
            </label>
            <Select value={country} onValueChange={handleCountryChange}>
              <SelectTrigger>
                <SelectValue placeholder={t('connect.selectCountry', { defaultValue: 'Select country' })} />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('connect.searchBanks', { defaultValue: 'Search banks...' })}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Banks list */}
        <div className="flex-1 min-h-0 overflow-y-auto mt-3 sm:mt-4 -mx-4 sm:-mx-6 px-4 sm:px-6 scrollbar-elegant">
          {banksQuery.isLoading ? (
            <div className="space-y-1 sm:space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg">
                  <Skeleton className="size-8 sm:size-10 rounded-lg" />
                  <div className="flex-1 space-y-1.5 sm:space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : banksQuery.isError ? (
            <div className="text-center py-6 sm:py-8 text-destructive text-sm">
              {t('connect.error', { defaultValue: 'Failed to load banks. Please try again.' })}
            </div>
          ) : filteredBanks.length === 0 ? (
            <div className="text-center py-6 sm:py-8 text-muted-foreground text-sm">
              {searchQuery
                ? t('connect.noResults', { defaultValue: 'No banks found matching your search.' })
                : t('connect.noBanks', { defaultValue: 'No banks available for this country.' })}
            </div>
          ) : (
            <div className="space-y-0.5 sm:space-y-1">
              {filteredBanks.map((bank) => {
                const isSelecting = selectedBank?.name === bank.name && authorizeMutation.isPending
                return (
                  <button
                    key={bank.bic || bank.name}
                    type="button"
                    onClick={() => void handleBankSelect(bank)}
                    disabled={authorizeMutation.isPending}
                    className={cn(
                      'w-full flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg text-left',
                      'hover:bg-muted/50 transition-colors',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      isSelecting && 'bg-muted'
                    )}
                  >
                    {/* Bank logo or placeholder */}
                    {bank.logo ? (
                      <img
                        src={bank.logo}
                        alt={bank.name}
                        className="size-8 sm:size-10 rounded-lg object-contain bg-white"
                        onError={(e) => {
                          // Replace with placeholder on error
                          e.currentTarget.style.display = 'none'
                          e.currentTarget.nextElementSibling?.classList.remove('hidden')
                        }}
                      />
                    ) : null}
                    <div
                      className={cn(
                        'size-8 sm:size-10 rounded-lg bg-muted flex items-center justify-center',
                        bank.logo && 'hidden'
                      )}
                    >
                      <Building2 className="size-4 sm:size-5 text-muted-foreground" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground truncate text-sm sm:text-base">{bank.name}</div>
                      {bank.bic && (
                        <div className="text-[10px] sm:text-xs text-muted-foreground font-mono">{bank.bic}</div>
                      )}
                    </div>

                    {isSelecting && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-3 sm:pt-4 border-t border-border -mx-4 sm:-mx-6 px-4 sm:px-6">
          <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
            {t('connect.cancel', { defaultValue: 'Cancel' })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ConnectBankModal
